import { useRef } from 'react';
import { string as yupString } from 'yup';

import { sanitizeAndNormalizeEmail } from '@utils/emailSanitizer';

import { ICheckEmailAsPoDTO } from '@interfaces/user/dto/userDTO';
import { IUserCheckEmail } from '@interfaces/user/user';

export const useEmailValidation = ({
	organizationId,
	defaultValue,
	checkEmailFn,
}: {
	organizationId?: string;
	defaultValue?: string;
	checkEmailFn: (data: ICheckEmailAsPoDTO) => Promise<IUserCheckEmail>;
}) => {
	const lastValidationRef = useRef<{ email: string; isValid: boolean } | null>(null);
	const requestSequenceRef = useRef(0);
	const normalizedDefaultEmail = sanitizeAndNormalizeEmail(defaultValue);

	const validateEmail = async (rawValue: string): Promise<boolean> => {
		const email = sanitizeAndNormalizeEmail(rawValue);

		if (!email) return true;

		const isFormatValid = await yupString().email().isValid(email);

		if (!isFormatValid) return true;

		if (email === normalizedDefaultEmail) return true;

		if (lastValidationRef.current?.email === email) {
			return lastValidationRef.current.isValid;
		}

		const currentRequestSeq = ++requestSequenceRef.current;

		try {
			const response = await checkEmailFn({ email, organizationId });

			const {
				alreadyExists = false,
				isInAnotherOrganization = false,
				isEmptyOrganizations = false,
			} = response;

			const isUniqueOrInAnotherOrganisation =
				!alreadyExists || isInAnotherOrganization || (!isInAnotherOrganization && isEmptyOrganizations);

			if (requestSequenceRef.current === currentRequestSeq) {
				lastValidationRef.current = { email, isValid: isUniqueOrInAnotherOrganisation };
			}

			return isUniqueOrInAnotherOrganisation;
		} catch {
			if (requestSequenceRef.current === currentRequestSeq) {
				lastValidationRef.current = { email, isValid: true };
			}
			return true;
		}
	};

	return { validateEmail };
};
